import React, { useState, useMemo } from "react";

// material-ui
import { Autocomplete, Tooltip } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import Ellipsis from "components/Table/Ellipsis";

// Functions
import pxToRem from "assets/theme/functions/pxToRem";

// Common components
import FontComponent from "components/Responsive/fonts";

// utils
import Constants, { Icons, Colors } from "utils/Constants";

// 3rd party
import PropTypes from "prop-types";

const getProjectSortLabel = (option = {}, getOptionLabel) => {
  const formattedLabel = (getOptionLabel(option) || "").trim();
  const [projectNumber = formattedLabel] = formattedLabel.split(/\s+[–-]\s+.+/);

  return projectNumber.trim().toLocaleLowerCase();
};

const startsWithDigit = (value = "") => /^\d/.test(value.trim());
const getOptionIdentity = (option = {}) => {
  if (option === null || option === undefined) return "";
  if (typeof option !== "object") return option.toString();

  return (option?.[Constants.MONGOOSE_ID] || option?.id || option?.value || "").toString();
};

const getOptionValue = (option = {}) => {
  if (option === null || option === undefined) return "";
  if (typeof option !== "object") return option.toString();

  return (getOptionIdentity(option) || option?.title || option?.name || "").toString();
};

const getPinnedProjectOptionRank = (option = {}, getOptionLabel) => {
  const optionId = (option?.[Constants.MONGOOSE_ID] || "").toString().trim().toLowerCase();
  const optionLabel = (getOptionLabel(option) || "").toString().trim().toLowerCase();

  // Keep select placeholder at the very top when present.
  if (optionId === "select" || optionLabel === "select") return 0;
  // Keep all next so users can still quickly switch to all-project mode where applicable.
  if (optionId === "all" || optionLabel === "all") return 1;
  // Keep the "Unassigned" filter option right below "All" so it stays discoverable.
  if (optionId === "notassigned" || optionLabel === "not assigned") return 2;
  // Keep "Other Project" at the very bottom.
  if (optionLabel === "other project") return 4;

  return 3;
};

function CustomAutoComplete({
  label,
  id,
  name,
  hint,
  handleChange,
  menu,
  error,
  helperText,
  getOptionLabel,
  value,
  labelStyle,
  valueStyle,
  disabled,
  mt,
  renderOption,
  loading,
  onOpen,
}) {
  const fontSize = FontComponent({ sizes: {} });
  const [inputValue, setInputValue] = useState("");

  const [isOpenFresh, setIsOpenFresh] = useState(true);

  const handleInputChange = (e, newInputValue, reason) => {
    if (reason === "input") setIsOpenFresh(false);
    setInputValue(newInputValue.trimStart());
  };

  const handleDropdownOpen = (...args) => {
    setIsOpenFresh(true);
    onOpen(...args);
  };

  const handleFilterOptions = (options, state) => {
    if (isOpenFresh) return options;
    const lower = state.inputValue.toLowerCase();
    if (!lower) return options;
    return options.filter((option) =>
      (state.getOptionLabel(option) || "").toLowerCase().includes(lower)
    );
  };

  const handleOptionChange = (e, selectedOption) => {
    handleChange({
      target: {
        name,
        value: selectedOption ? getOptionValue(selectedOption) : "",
      },
    });
  };

  const isProjectDropdown = useMemo(() => {
    const l = (label || "").toLowerCase();
    const n = (name || "").toLowerCase();
    const h = (hint || "").toLowerCase();
    return l.includes("project") || n === "project" || h.includes("project");
  }, [label, name, hint]);

  const sortedMenu = useMemo(() => {
    if (!menu || !Array.isArray(menu)) return [];

    if (isProjectDropdown) {
      const menuCopy = [...menu];
      menuCopy.sort((a, b) => {
        const pinnedRankA = getPinnedProjectOptionRank(a, getOptionLabel);
        const pinnedRankB = getPinnedProjectOptionRank(b, getOptionLabel);

        if (pinnedRankA !== pinnedRankB) return pinnedRankA - pinnedRankB;

        const sortLabelA = getProjectSortLabel(a, getOptionLabel);
        const sortLabelB = getProjectSortLabel(b, getOptionLabel);

        // Letter-based project codes should appear before number-based entries like dates.
        if (startsWithDigit(sortLabelA) && !startsWithDigit(sortLabelB)) return 1;
        if (!startsWithDigit(sortLabelA) && startsWithDigit(sortLabelB)) return -1;

        return sortLabelA.localeCompare(sortLabelB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
      return menuCopy;
    }

    return menu;
  }, [menu, isProjectDropdown, getOptionLabel]);

  const resolvedValue = useMemo(() => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value !== "object") {
      return (
        sortedMenu.find(
          (option) =>
            getOptionValue(option) === value.toString() ||
            (getOptionLabel(option) || "").trim() === value.toString()
        ) || null
      );
    }

    const valueId = getOptionIdentity(value);
    const valueLabel = (getOptionLabel(value) || "").trim();

    if (!valueId && !valueLabel) {
      return null;
    }

    return (
      sortedMenu.find((option) => {
        const optionId = getOptionIdentity(option);

        if (valueId && optionId && optionId === valueId) {
          return true;
        }

        return valueLabel !== "" && (getOptionLabel(option) || "").trim() === valueLabel;
      }) || value
    );
  }, [value, sortedMenu, getOptionLabel]);

  return (
    <>
      <MDTypography
        variant="caption"
        sx={{
          fontSize,
          fontWeight: labelStyle?.fontWeight || 500,
          color: "#344054",
          marginBottom: "4px",
        }}
      >
        {label}
      </MDTypography>

      <Autocomplete
        options={sortedMenu}
        getOptionLabel={getOptionLabel}
        name={name}
        id={id}
        variant="standard"
        value={resolvedValue}
        disabled={disabled}
        inputValue={inputValue}
        loading={loading}
        onOpen={handleDropdownOpen}
        filterOptions={handleFilterOptions}
        style={{ backgroundColor: valueStyle?.backgroundColor || "transparent" }}
        onInputChange={handleInputChange}
        onChange={handleOptionChange}
        isOptionEqualToValue={(option, selectedValue) => {
          const optionId = getOptionIdentity(option);
          const selectedId = getOptionIdentity(selectedValue);

          if (optionId && selectedId && optionId === selectedId) {
            return true;
          }

          return (getOptionLabel(option) || "") === (getOptionLabel(selectedValue) || "");
        }}
        renderOption={
          renderOption ||
          ((props, option) => {
            if (option?.hide) {
              return null;
            }
            return (
              <li {...props} key={option?.[Constants.MONGOOSE_ID]}>
                <Ellipsis
                  maxWidth="100%"
                  title={option?.title || option?.name}
                  placement="right"
                  style={{
                    textTransform: valueStyle?.textTransform || "",
                    fontSize,
                    color: disabled ? "#8c8ca8" : valueStyle?.color || "",
                  }}
                >
                  {option?.title || option?.name}
                </Ellipsis>
              </li>
            );
          })
        }
        componentsProps={{
          paper: {
            sx: {
              width: valueStyle?.menuWidth || "max-content",
              minWidth: "100%",
              maxWidth: { xs: "90vw", sm: "600px" },
              backgroundColor: Colors.WHITE,
            },
          },
        }}
        sx={{
          "& .MuiAutocomplete-inputRoot": {
            display: "flex",
            minWidth: valueStyle?.minWidth,
            alignItems: "center",
            height: valueStyle?.height || "auto",
            padding: valueStyle?.padding ? valueStyle?.padding : "4px",
            fontSize,
            backgroundColor: disabled
              ? Colors.DISABLED
              : valueStyle?.backgroundColor || "transparent",
          },
          mt,
        }}
        popupIcon={Icons.ARROW_DOWN}
        renderInput={(params) => {
          const selectedLabel = resolvedValue ? getOptionLabel(resolvedValue) : "";

          const showTooltip = selectedLabel.length > 10;

          const inputField = (
            <MDInput
              {...params}
              name={name}
              placeholder={hint}
              error={Boolean(error)}
              helperText={helperText}
              disabled={disabled}
              sx={{
                "& .MuiInputBase-input": {
                  color: disabled ? "#8c8ca8" : valueStyle?.color || "",
                  textTransform: valueStyle?.textTransform || "",
                  fontSize,
                  backgroundColor: disabled ? Colors.DISABLED : "transparent",
                  fontWeight: valueStyle?.fontWeight || 500,
                },
              }}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  color: "#FF2E2E",
                },
              }}
            />
          );

          return showTooltip ? (
            <Tooltip title={selectedLabel} arrow placement="top">
              <div>{inputField}</div>
            </Tooltip>
          ) : (
            inputField
          );
        }}
      />
    </>
  );
}

CustomAutoComplete.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  handleChange: PropTypes.func,
  menu: PropTypes.arrayOf(PropTypes.object),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  getOptionLabel: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  labelStyle: PropTypes.objectOf(PropTypes.any),
  valueStyle: PropTypes.objectOf(PropTypes.any),
  disabled: PropTypes.bool,
  mt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderOption: PropTypes.func, // <-- Added propType for renderOption
  loading: PropTypes.bool,
  onOpen: PropTypes.func,
};

CustomAutoComplete.defaultProps = {
  handleChange: () => {},
  menu: [],
  error: false,
  helperText: "",
  value: "",
  labelStyle: {},
  valueStyle: {},
  disabled: false,
  mt: pxToRem(0),
  renderOption: null, // <-- Default to null, no custom renderOption
  loading: false,
  onOpen: () => {},
};

export default CustomAutoComplete;
